import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { concatAll, map, toArray, concatMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { BaseListDomainService } from '@common/domain/base-list-domain.service';
import * as fromBckpCapacitiesActions from '@apps/bckp/store/capacities/capacities.actions';
import { ResultApi } from '@common/api/result-api';
import { ChargedCapacityApi } from '@apps/bckp/api/models/charged-capacity-api';

import { ChargedCapacityListApiService } from '../api/charged-capacity-list-api.service';
import { NamesListApiService } from '../api/names-list-api.service';
import { BckpConstants } from '../bckp.constants';
import { FlatCapacityPerServer, ChargedCapacity, CapacityGbytesPerServer } from './models';
import { IAuroraState } from '../../aurora.state';
import { PolicyForFilter } from './models';

@Injectable({
    providedIn: 'root'
})
export class ChargedCapacityListDomainService extends BaseListDomainService
{
    constructor (
        private chargedCapacityListApiService: ChargedCapacityListApiService,
        private namesListApiService: NamesListApiService,
        private store: Store<IAuroraState>
    )
    {
        super();
    }

    getChargedServers (since: string, until: string): Observable<string[][]>
    {
        return this.namesListApiService.getImagesServers(since, until)
            .pipe(
                map((servers: string[]) =>
                {
                    const lgr = BckpConstants.NbServerPerChargedCapacity;
                    const initLgr = servers.length;

                    let startIdx = 0;
                    const lists: string[][] = [];

                    do
                    {
                        lists.push(servers.slice(startIdx, startIdx + lgr));

                        startIdx += lgr;
                    } while (startIdx < initLgr);

                    return lists;
                })
            );
    }

    public getChargedCapacities (since: string, until: string): Observable<CapacityGbytesPerServer[]>
    {
        return this.getChargedServers(since, until)
            .pipe(
                concatAll(),
                concatMap((servers: string[]) =>
                    this.chargedCapacityListApiService.getChargedCapacities(since, until, servers.join(','))
                ),
                map((obj: ResultApi<ChargedCapacityApi>): ChargedCapacityApi[] =>
                    obj.value
                ),
                concatAll(),
                map(ChargedCapacity.mapperFromApi),
                map(ChargedCapacity.flattenToPerServer),
                concatAll(),
                toArray(),
                map((capas: FlatCapacityPerServer[]): CapacityGbytesPerServer[] =>
                {
                    // Recherche des politiques uniques et triées, préparation des filtres
                    const policyNames: string[] = this.getUniqueSortedPolicyNames(capas);
                    const policyForFilters = policyNames.map((p: string, idx: number): PolicyForFilter =>
                        new PolicyForFilter(p, Math.pow(2, idx))
                    );
                    // ! Push de Total pour qu'il soit bien le dernier du tableau
                    policyNames.push(BckpConstants.TotalColumnName);
                    policyForFilters.push(new PolicyForFilter(BckpConstants.TotalColumnName, 0));

                    const fullFlags: number = policyForFilters
                        .reduce((prev: number, curr: PolicyForFilter): number => prev + curr.flag , 0 );
                    this.store.dispatch(
                        fromBckpCapacitiesActions.BckpCapacitiesPoliciesLoaded({
                            policyForFilters,
                            fullFlags
                        })
                    );

                    // Index des politiques une fois triées
                    const idxPolicies: Map<string, number> = this.getPoliciesIndex(policyNames);

                    // Réorganisation des données des serveurs
                    return this.transormToPerServer(capas, idxPolicies, policyForFilters);

                })
            );
    }

    private getUniqueSortedPolicyNames = (flatCapacities: FlatCapacityPerServer[]): string[] =>
        flatCapacities
            .filter((flatCapacityPerServer: FlatCapacityPerServer): boolean =>
                flatCapacityPerServer.policy !== BckpConstants.TotalColumnName
            )
            .map((flatCapacityPerServer: FlatCapacityPerServer): string =>
                flatCapacityPerServer.policy
            )
            .reduce((prev: string[], curr: string): string[] =>
            {
                if (!prev.includes(curr))
                {
                    prev.push(curr);
                }

                return prev;
            }, [])
            .sort((pola, polb) =>
                pola.toUpperCase().localeCompare(polb.toUpperCase())
            );


    /**
     * Index des politiques une fois triées
     */
    private getPoliciesIndex (policyNames: string[]): Map<string, number>
    {
        const idxPolicies = new Map<string, number>();

        policyNames.forEach((val: string, idx: number): Map<string, number> =>
            idxPolicies.set(val, idx)
        );

        return idxPolicies;
    }

    private transormToPerServer (capas: FlatCapacityPerServer[], idxPolicies: Map<string, number>, policyForFilters: PolicyForFilter[])
    {
        const servers = new Map<string, number[]>();
        const flags = new Map<string, number>();

        capas.forEach((capa: FlatCapacityPerServer): void =>
        {
            const idx = idxPolicies.get(capa.policy);
            const policyFlag = policyForFilters[idx];

            if (!servers.has(capa.serverShort))
            {
                servers.set(capa.serverShort, []);
            }

            if (!flags.has(capa.serverShort))
            {
                flags.set(capa.serverShort, 0);
            }

            const tmp = flags.get(capa.serverShort);
            flags.set(capa.serverShort, tmp + policyFlag.flag);
            servers.get(capa.serverShort)[idx] = capa.gbytes;
        });

        const capacityGbytesPerServers: CapacityGbytesPerServer[] = [];

        servers.forEach((value: number[], key: string): void =>
        {
            const cgps = new CapacityGbytesPerServer(key, value);
            cgps.policyFlags = flags.get(key);

            capacityGbytesPerServers.push(cgps);
        });

        return capacityGbytesPerServers;
    }
}
